import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { AccountingEnums } from "models/enums";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { AccountingFormTypes } from "models/formTypes";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
import { ReportHanlder } from "models/propTypes/accountingPropTypes";
//#endregion
//#region components
import { DashboardButtonGroup, FilterButton, PageLayouts, StyledButton, ReloadEvent } from "components/common";
import { Grid } from "@mui/material";
import StandardIncomeStatement from "../resultReport/standard";
import MonthlyIncomeStatement from "../resultReport/monthly";
import IncomeStatementByTags from "../resultReport/tag";
import { getPeriodSearch } from "./reportTypeSwitchFilter";
import useMonthlyReportDefaults from "../hooks/useMonthlyReportDefaults";
import useFinancialReportFilterDefinition from "../forms/useReportFilterDefinition";
import ExpandAllFilterControl from "../expandAllFilterControl";
import { useQueryStateEnum } from "routing/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { useFinancialReportService } from "serviceHooks";
//#endregion
import { EnumHelper, LocalizationHelperInstance, ExportHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const ResultReport: React.FC = (props) => {
  const financialReportService = useFinancialReportService();
  const companyName = useSelector((store: AppState) => store.auth.selectedCompany.name ?? "");
  const companyOrg = useSelector((store: AppState) => store.auth.selectedCompany.org ?? "");

  const reportRef = React.useRef<ReportHanlder>(null);
  const { getDefaultMonthRange } = useMonthlyReportDefaults();

  const location = useLocation();
  const navigate = useNavigate();

  const [reportType, setReportType] = useQueryStateEnum<AccountingEnums.FinancialReportType>(
    "reportType",
    AccountingEnums.FinancialReportType.Standard
  );
  const [disableExport, setDisableExport] = React.useState<boolean>(false);

  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Accounting.IncomeStatement);
  const reportTypeName = LocalizationHelperInstance.getFormattedMessage(`Accounting.FinancialReportType_${reportType}`);

  const [exportData, setExportData] = React.useState<any[][]>([]);

  const onReportTypeButtonClick = (type: AccountingEnums.FinancialReportType) => {
    location.search = getPeriodSearch(
      location.search,
      FormFieldConstants.Accounting.FinancialReportFilter.MonthRange,
      getDefaultMonthRange().monthRange
    );
    navigate(location, { replace: true });
    setReportType(type);
    setDisableExport(false);
  };

  const reportMap = {
    [AccountingEnums.FinancialReportType.Standard]: StandardIncomeStatement,
    [AccountingEnums.FinancialReportType.Monthly]: MonthlyIncomeStatement,
    [AccountingEnums.FinancialReportType.Tag]: IncomeStatementByTags,
  };

  const ReportComponent = reportMap[reportType];

  const {
    filterFormFields,
    filterQueryStateHandler,
    apiToReportFilterFormatter,
    apiToReportMonthlyFilterFormatter,
    apiToReportDimensionsFilterFormatter,
  } = useFinancialReportFilterDefinition(reportType);

  function exportReport(fileEnding: string) {
    const filename = `${title} - ${companyName} - ${reportTypeName}.${fileEnding}`;

    ExportHelper.exportReport(exportData, filename, title);

    return Promise.resolve();
  }

  function exportReportExcel() {
    return exportReport("xlsx");
  }

  function exportReportCSV() {
    return exportReport("csv");
  }

  const exportReportPDF = async () =>
    reportRef.current?.print(title, `${companyName} | ${companyOrg}`, `${title} - ${companyName} - ${reportTypeName}`);

  const getFilterProps = (reportType: AccountingEnums.FinancialReportType) => {
    switch (reportType) {
      case AccountingEnums.FinancialReportType.Standard:
        return {
          apiToReportFilterFormatter: apiToReportFilterFormatter,
          reportToAPIFilterFormatter: financialReportService.standardReportWithComparisonColumnsToAPIFilter,
          filterCategory: WintSuperkollAPIModelCustomFilters.FilterCategory.ResultReport,
        };
      case AccountingEnums.FinancialReportType.Monthly:
        return {
          apiToReportFilterFormatter: apiToReportMonthlyFilterFormatter,
          reportToAPIFilterFormatter: financialReportService.monthlyReportToAPIFilter,
          filterCategory: WintSuperkollAPIModelCustomFilters.FilterCategory.ResultReportMonthly,
        };
      case AccountingEnums.FinancialReportType.Tag:
        return {
          apiToReportFilterFormatter: apiToReportDimensionsFilterFormatter,
          reportToAPIFilterFormatter: financialReportService.dimensionReportToAPIFilter,
          filterCategory: WintSuperkollAPIModelCustomFilters.FilterCategory.ResultReportDimension,
        };
    }
  };

  const disableExportButton = (disable: boolean) => setDisableExport(disable);

  return (
    <ReloadEvent
      render={(reloadKey, reload) => (
        <PageLayouts.DashboardLayout<AccountingFormTypes.FinancialReportFilter>
          key={reportType}
          entityKey={0}
          title={title}
          topRightButtons={
            <DashboardButtonGroup
              buttons={(
                EnumHelper.getNumericItemValues(AccountingEnums.FinancialReportType).map((i) => (
                  <StyledButton
                    size="sm"
                    onClick={onReportTypeButtonClick.bind(null, i)}
                    className={reportType !== i ? cssExports.transparent : ""}
                  >
                    {LocalizationHelperInstance.getFormattedMessage(`Accounting.FinancialReportType_${i}`)}
                  </StyledButton>
                )) as (React.ReactElement | typeof ACTION_SEPARATOR)[]
              ).concat([ACTION_SEPARATOR, <FilterButton />, <ExpandAllFilterControl reportRef={reportRef} />])}
              moreActions={
                !disableExport
                  ? [
                      {
                        description: LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.Common.ExportToExcel
                        ),
                        icon: IconDefinitionConstants.WSave,
                        onClickHandler: exportReportExcel,
                      },
                      {
                        description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ExportToCSV),
                        icon: IconDefinitionConstants.WSave,
                        onClickHandler: exportReportCSV,
                      },
                      {
                        description: LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.Common.DownloadAsPDF
                        ),
                        icon: IconDefinitionConstants.WDownload,
                        onClickHandler: exportReportPDF,
                        hidden: reportRef?.current === undefined,
                      },
                    ]
                  : undefined
              }
            />
          }
          filterFields={filterFormFields}
          filterQueryStateHandler={filterQueryStateHandler}
          {...getFilterProps(reportType)}
        >
          <Grid item container>
            <ReportComponent
              ref={reportRef}
              updateReportExportData={setExportData}
              setDisableExport={disableExportButton}
              reloadKey={reloadKey}
              reload={reload}
            />
          </Grid>
        </PageLayouts.DashboardLayout>
      )}
    />
  );
};

export default ResultReport;
